import { v4 as uuid } from 'uuid';
import { Vector2 } from 'three';
import { Areas, InterAreaConnection, LogicalSpace, Obstacle, Templates } from '@thive/fddm-types';

import { assertNever, merge } from '@/helpers/utils';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { Area } from '@/modules/floorplanDesign/types';
import { incrementName } from '@/modules/floorplanDesign/helpers/name';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import {
  areasToFlatCollection,
  createEmptyAreas,
  hasNoAreas,
  offsetSegments,
  pushAreasToItsCollection,
  pushAreaToItsCollection,
  templatesToFlatCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { areaFactory } from '@/modules/floorplanDesign/factories';
import { COPY_PASTE_OFFSET_DEFAULT } from '@/modules/copy/constants';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { useCopyPasteStore } from '@/modules/copy/store/useCopyPasteStore';
import { logger } from '@/modules/common/logging/logger';
import { computeItemsBoundingBoxExtent } from '@/modules/common/helpers/boundingBox';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { IAppService } from '@/modules/app/service/IAppService';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { ICanvasService } from '@/modules/canvas/services/Canvas/ICanvasService';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { IHistoryService } from '@/modules/canvas/services/history/IHistoryService';

import { CopyIdMapping } from '../types';
import { ICopyPasteService } from './ICopyPasteService';

export class CopyPasteServiceImpl implements ICopyPasteService {
  private appStore: typeof useAppStore;
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private copyPasteStore: typeof useCopyPasteStore;
  private fddmSvc: IFddmService;
  private appSvc: IAppService;
  private canvasSvc: ICanvasService;
  private historySvc: IHistoryService;

  public constructor(
    copyPasteStore: typeof useCopyPasteStore,
    appStore: typeof useAppStore,
    floorplanDesignStore: typeof useFloorplanDesignStore,
  ) {
    this.copyPasteStore = copyPasteStore;
    this.appStore = appStore;
    this.floorplanDesignStore = floorplanDesignStore;
    this.fddmSvc = FddmServiceFactory.getInstance().createFddmService();
    this.appSvc = AppServiceFactory.getInstance().createAppService();
    this.canvasSvc = CanvasServiceFactory.getInstance().createCanvasService();
    this.historySvc = HistoryServiceFactory.getInstance().createHistoryService();
  }

  public copyItem<T extends Area | LogicalSpace | Obstacle>(
    params:
      | {
          item: Area;
          type: 'Area';
          offset: Vector2;
        }
      | {
          item: LogicalSpace;
          type: 'LogicalSpace';
          offset: Vector2;
        }
      | {
          item: Obstacle;
          type: 'Obstacle';
          offset: Vector2;
        },
  ): T {
    const { item, offset, type } = params;

    switch (type) {
      case 'LogicalSpace':
        return merge(
          {
            ...item,
            name: incrementName(item.name ?? item.id),
            id: uuid(),
            polygon: {
              ...item.polygon,
              points: item.polygon.points.map((point) => ({
                x: point.x + offset.x,
                y: point.y + offset.y,
              })),
            },
          },
          {},
        ) as T;
      case 'Obstacle': {
        const segmentId = uuid();
        return merge(
          {
            ...item,
            name: incrementName(item.name ?? item.id),
            id: uuid(),
            segments: minItemsArrayUtils.mapAtLeast1(
              offsetSegments(item.segments, offset),
              (segment, index) => ({
                ...segment,
                id: `${segmentId}.${index}`,
              }),
            ),
          },
          {},
        );
      }
      case 'Area':
        if (isHighwayArea(item)) {
          const segmentId = uuid();
          return areaFactory.copyArea(item, {
            name: incrementName(item.name ?? item.id),
            id: uuid(),
            segments: minItemsArrayUtils.mapAtLeast1(
              offsetSegments(item.segments, offset),
              (segment, index) => ({
                ...segment,
                id: `${segmentId}.${index}`,
              }),
            ),
            cutOutIds: [],
          }) as T;
        }

        return areaFactory.copyArea(item, {
          name: incrementName(item.name ?? item.id),
          id: uuid(),
          rectangle: {
            ...item.rectangle,
            centerX: item.rectangle.centerX + offset.x,
            centerY: item.rectangle.centerY + offset.y,
          },
        }) as T;
      default:
        assertNever(type);
    }
  }

  public copyToMemory(ids: string[]): void {
    this.copyPasteStore.getState().setIds(ids);
  }

  public copySelectionToMemory(): void {
    const { selectedIds } = this.appStore.getState();
    this.copyPasteStore.getState().setIds(selectedIds);
  }

  public pasteFromMemory(canvasDestination: Vector2 | null = null): void {
    this.paste(this.copyPasteStore.getState().getIds(), canvasDestination);
  }

  public pasteFromSelection(canvasDestination: Vector2 | null = null): void {
    this.paste(this.appStore.getState().selectedIds, canvasDestination);
  }

  public duplicateSelection(): void {
    const { selectedIds } = this.appStore.getState();
    this.paste(selectedIds);
  }

  // #region Private methods

  private copyGroup(
    originalGroupIds: string[],
    offset: Vector2 = COPY_PASTE_OFFSET_DEFAULT,
  ): { newTemplates: Templates; newAreas: Areas; idMapping: CopyIdMapping } {
    const originalTemplates = this.floorplanDesignStore
      .getState()
      .getTemplatesByIds(originalGroupIds);
    const idMapping: CopyIdMapping = [];
    const newAreas = createEmptyAreas();
    const newTemplates: Templates = {
      lassies: [],
      shuttleRacks: [],
      lassieMinis: [],
    };

    // TODO: replace this with userGroups i guess?
    // originalGroups.functionalGroups.forEach((group) => {
    //   const copyResult = copyAreas(group.areaIds, offset);
    //   const areas = areasToFlatCollection(copyResult.newAreas);
    //   const areaIds = areas.map((area) => area.id);
    //   const newGroup = createCustomGroupFromAreaIds(areaIds, group.name);
    //   if (newGroup !== undefined) {
    //     pushGroupToItsCollection(newGroups, newGroup);
    //     pushAreasToItsCollection(newAreas, copyResult.newAreas);
    //     idMapping.push(...copyResult.idMapping);
    //   }
    // });

    return {
      newTemplates,
      newAreas,
      idMapping,
    };
  }

  private copyAreas(
    originalIds: string[],
    offset: Vector2 = COPY_PASTE_OFFSET_DEFAULT,
  ): { newAreas: Areas; idMapping: CopyIdMapping } {
    const originAreas = this.floorplanDesignStore.getState().getAreasByIds(originalIds);
    const idMapping: CopyIdMapping = [];
    const newAreas = createEmptyAreas();

    areasToFlatCollection(originAreas).forEach((originArea) => {
      const newArea = this.copyItem<Area>({
        item: originArea,
        type: 'Area',
        offset,
      });

      if (isHighwayArea(originArea) && isHighwayArea(newArea)) {
        originArea.segments.forEach((segment, index) => {
          idMapping.push({ newShapeId: newArea.segments[index].id, oldShapeId: segment.id });
        });
        pushAreaToItsCollection(newAreas, newArea);
      } else {
        idMapping.push({ newShapeId: newArea.id, oldShapeId: originArea.id });
        pushAreaToItsCollection(newAreas, newArea);
      }
    });

    return {
      newAreas,
      idMapping,
    };
  }

  private copyConnections(idMapping: CopyIdMapping): boolean {
    const oldShapeIds = idMapping.map((item) => item.oldShapeId);
    const allConnections = this.floorplanDesignStore.getState().interAreaConnectionActions.getAll();
    const newConnections: InterAreaConnection[] = [];

    this.filterRelatedConnections(allConnections, oldShapeIds).forEach((connection) => {
      const fromMapping = idMapping.find((item) => item.oldShapeId === connection.idA);
      const toMapping = idMapping.find((item) => item.oldShapeId === connection.idB);

      if (!fromMapping || !toMapping) {
        logger.error('Could not find mapping for connection', connection);
        return null;
      }

      newConnections.push({
        ...connection,
        id: uuid(),
        idA: fromMapping.newShapeId,
        idB: toMapping.newShapeId,
      });
    });

    if (newConnections.length !== 0) return this.fddmSvc.addInterAreaConnections(newConnections);

    return false;
  }

  private filterRelatedConnections(connections: InterAreaConnection[], shapeIds: string[]) {
    const ids = new Set(shapeIds);
    return connections.filter((item) => ids.has(item.idA || item.idB));
  }

  private copyJitRoutingConfigs(idMapping: CopyIdMapping): void {
    const oldShapeIdSet = new Set(idMapping.map((m) => m.oldShapeId));
    const { design } = this.floorplanDesignStore.getState();

    const newJitConfigs = design.justInTimeRoutingConfigs
      .filter((config) => oldShapeIdSet.has(config.highwayAreaId))
      .map((config) => {
        const highwayMapping = idMapping.find((m) => m.oldShapeId === config.highwayAreaId);
        return {
          ...config,
          id: uuid(),
          highwayAreaId: highwayMapping ? highwayMapping.newShapeId : config.highwayAreaId,
          targetAreaIds: minItemsArrayUtils.mapAtLeast1(
            config.targetAreaIds,
            (id) => idMapping.find((m) => m.oldShapeId === id)?.newShapeId ?? id,
          ),
          waitAreaIds: config.waitAreaIds.map(
            (id) => idMapping.find((m) => m.oldShapeId === id)?.newShapeId ?? id,
          ),
        };
      });

    if (newJitConfigs.length > 0) {
      this.fddmSvc.addJitRoutingConfigs(newJitConfigs);
    }
  }

  private paste(ids: string[], canvasDestination: Vector2 | null = null): void {
    this.historySvc.batch(async () => {
      this.appSvc.resetSelection();

      const fddmStore = this.floorplanDesignStore.getState();
      let offset = COPY_PASTE_OFFSET_DEFAULT;
      const obstacles = fddmStore.getObstaclesByIds(ids);
      const logicalSpaces = fddmStore.getLogicalSpacesByIds(ids);
      const newSelectedIds: string[] = [];

      // calculate offset if canvasDestination is provided
      if (canvasDestination) {
        const templates = fddmStore.getTemplatesByIds(ids);
        const areasInTemplates = templatesToFlatCollection(templates).flatMap(
          (template) => template.group.areaIds,
        );
        const areas = fddmStore.getAreasByIds([...ids, ...areasInTemplates]);
        const origin = computeItemsBoundingBoxExtent(areas, obstacles, logicalSpaces);
        offset = new Vector2(canvasDestination.x - origin.x, canvasDestination.y - origin.y);
      }

      // areas and groups
      const pastedGroups = this.copyGroup(ids, offset);
      const newTemplateIds = templatesToFlatCollection(pastedGroups.newTemplates).map(
        (item) => item.group.id,
      );
      newSelectedIds.push(...newTemplateIds);

      const { idMapping, newAreas: pastedAreas } = this.copyAreas(ids, offset);
      newSelectedIds.push(...areasToFlatCollection(pastedAreas).map((item) => item.id));
      if (!hasNoAreas(pastedAreas)) {
        pushAreasToItsCollection(pastedAreas, pastedGroups.newAreas);
        await this.fddmSvc.addAreas(pastedAreas);
        this.fddmSvc.addTemplates(pastedGroups.newTemplates);
      }

      // connections, called after areas are added to the store
      this.copyConnections(idMapping);

      // jit routing configs for copied highways
      this.copyJitRoutingConfigs(idMapping);

      // logical spaces
      const pastedLogicalSpaces = logicalSpaces.map((logicalSpace) =>
        this.copyItem<LogicalSpace>({
          item: logicalSpace,
          type: 'LogicalSpace',
          offset,
        }),
      );
      if (pastedLogicalSpaces.length !== 0) {
        await this.fddmSvc.addLogicalSpaces(pastedLogicalSpaces);
        newSelectedIds.push(...pastedLogicalSpaces.map((item) => item.id));
      }

      // obstacles
      const pastedObstacles = obstacles.map((obstacle) =>
        this.copyItem<Obstacle>({
          item: obstacle,
          type: 'Obstacle',
          offset,
        }),
      );
      if (pastedObstacles.length !== 0) {
        await this.fddmSvc.addObstacles(pastedObstacles);
        newSelectedIds.push(...pastedObstacles.map((item) => item.id));
      }

      this.appSvc.setSelectedIds(newSelectedIds);
    });
  }

  // #endregion
}
